import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Bot,
  User,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  sendChatMessage,
  type ChatMessage,
  type AIResponse,
  type ExtractedEvent,
  type ExtractedOrganizer,
  type ExtractedMember,
  type ExtractedTeam,
} from "@/services/aiService";
import { useOrganizers, useCreateOrganizer } from "@/hooks/useOrganizers";
import { useEvents, useCreateEvent } from "@/hooks/useEvents";
import {
  useMembers,
  useCreateMember,
  useUpdateMember,
} from "@/hooks/useMembers";
import { useTeams, useCreateTeam } from "@/hooks/useTeams";
import { useAddTeamMember } from "@/hooks/useTeamMembers";
import { useToast } from "@/hooks/useToast";
import type {
  OrganizerCategory,
  EventFormat,
  TeamCategory,
  UserRole,
} from "@/types/database";

interface AIAssistantDialogProps {
  open: boolean;
  onClose: () => void;
  onEventCreated?: (eventId: string) => void;
}

interface DisplayMessage {
  role: "user" | "assistant";
  content: string;
  extractedData?: AIResponse;
}

export function AIAssistantDialog({
  open,
  onClose,
  onEventCreated,
}: AIAssistantDialogProps) {
  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentExtraction, setCurrentExtraction] = useState<AIResponse | null>(
    null,
  );
  const [isCreating, setIsCreating] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: organizers = [] } = useOrganizers();
  const { data: members = [] } = useMembers();
  const { data: teams = [] } = useTeams();
  const { data: events = [] } = useEvents();

  const createOrganizer = useCreateOrganizer();
  const createMember = useCreateMember();
  const updateMember = useUpdateMember();
  const createTeam = useCreateTeam();
  const addTeamMember = useAddTeamMember();
  const createEvent = useCreateEvent();
  const { toast } = useToast();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          role: "assistant",
          content:
            "Bonjour ! Je peux vous aider à créer des événements, ajouter des membres au collectif, ou constituer des équipes. Collez un email, décrivez un appel téléphonique, ou donnez-moi les informations que vous avez. Je m'occupe d'extraire les détails importants !",
        },
      ]);
    }
  }, [open, messages.length]);

  const handleReset = () => {
    setMessages([]);
    setCurrentExtraction(null);
    setInput("");
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");

    const newMessages: DisplayMessage[] = [
      ...messages,
      { role: "user", content: userMessage },
    ];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      const chatHistory: ChatMessage[] = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const existingOrganizers = organizers.map((o) => ({
        id: o.id,
        name: o.name,
      }));

      const existingMembers = members.map((m) => ({
        id: m.id,
        full_name: m.full_name || m.email,
        email: m.email,
      }));

      const existingTeams = teams.map((t) => ({
        id: t.id,
        name: t.name,
      }));

      const existingEvents = events.map((e) => ({
        id: e.id,
        name: e.name,
        event_date: e.event_date,
        location: e.location,
        organizer_name: organizers.find((o) => o.id === e.organizer_id)?.name,
      }));

      const response = await sendChatMessage(
        chatHistory,
        existingOrganizers,
        existingMembers,
        existingTeams,
        existingEvents,
      );

      setCurrentExtraction(response);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: response.assistant_message,
          extractedData: response,
        },
      ]);
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error instanceof Error ? error.message : "Une erreur est survenue",
        variant: "destructive",
      });
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: "Désolé, une erreur est survenue. Pouvez-vous réessayer ?",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCreateEntities = async () => {
    if (!currentExtraction?.ready_to_create) return;

    setIsCreating(true);

    try {
      // Parser les correspondances de membres existants
      const memberMatches: { [key: string]: string } = {};
      if (currentExtraction.existing_member_matches) {
        try {
          Object.assign(
            memberMatches,
            JSON.parse(currentExtraction.existing_member_matches),
          );
        } catch (e) {
          console.error("Failed to parse member matches:", e);
        }
      }

      // Créer ou mettre à jour les membres
      if (currentExtraction.members && currentExtraction.members.length > 0) {
        for (const memberData of currentExtraction.members as ExtractedMember[]) {
          if (!memberData.email) continue;

          const existingMemberId = memberMatches[memberData.email];

          if (existingMemberId) {
            // Mettre à jour le membre existant
            await updateMember.mutateAsync({
              id: existingMemberId,
              member: {
                full_name: memberData.full_name || undefined,
                phone: memberData.phone || undefined,
                role: (memberData.role as UserRole) || undefined,
              },
            });
            toast({
              title: "Membre mis à jour",
              description: `${memberData.full_name || memberData.email} a été mis à jour.`,
            });
          } else {
            // Créer un nouveau membre
            await createMember.mutateAsync({
              id: crypto.randomUUID(),
              email: memberData.email,
              full_name: memberData.full_name || null,
              phone: memberData.phone || null,
              role: (memberData.role as UserRole) || "musician",
            });
            toast({
              title: "Membre créé",
              description: `${memberData.full_name || memberData.email} a été ajouté au collectif.`,
            });
          }
        }
      }

      // Créer une équipe si demandé
      if (currentExtraction.team?.name) {
        const teamData = currentExtraction.team as ExtractedTeam;
        let teamId = currentExtraction.existing_team_match;

        if (!teamId) {
          const newTeam = await createTeam.mutateAsync({
            name: teamData.name!,
            category: (teamData.category as TeamCategory) || "Mixed",
            description: teamData.description || null,
          });
          teamId = newTeam.id;
          toast({
            title: "Équipe créée",
            description: `L'équipe "${teamData.name}" a été créée.`,
          });
        }

        // Ajouter les membres à l'équipe
        if (
          teamData.member_emails &&
          teamData.member_emails.length > 0 &&
          teamId
        ) {
          for (const email of teamData.member_emails) {
            const existingMemberId = memberMatches[email];
            if (existingMemberId) {
              await addTeamMember.mutateAsync({
                team_id: teamId,
                user_id: existingMemberId,
                role_in_team: null,
                instrument: null,
                is_leader: false,
              });
            }
          }
          toast({
            title: "Membres affectés",
            description: `Les membres ont été ajoutés à l'équipe "${teamData.name}".`,
          });
        }
      }

      // Créer un événement si demandé
      let organizerId = currentExtraction.existing_organizer_match;

      if (!organizerId && currentExtraction.organizer?.name) {
        const orgData = currentExtraction.organizer as ExtractedOrganizer;
        const newOrganizer = await createOrganizer.mutateAsync({
          name: orgData.name!,
          category: (orgData.category as OrganizerCategory) || "Association",
          contact_person: orgData.contact_person || null,
          email: orgData.email || null,
          phone: orgData.phone || null,
          address: orgData.address || null,
        });
        organizerId = newOrganizer.id;

        toast({
          title: "Organisateur créé",
          description: `L'organisateur "${orgData.name}" a été créé.`,
        });
      }

      if (currentExtraction.event && organizerId) {
        const eventData = currentExtraction.event as ExtractedEvent;
        const newEvent = await createEvent.mutateAsync({
          name: eventData.name || "Nouvel événement",
          event_date:
            eventData.event_date || new Date().toISOString().split("T")[0],
          event_time: eventData.event_time || null,
          location: eventData.location || "À définir",
          event_type: eventData.event_type || ["Autre"],
          event_format: "Prestation" as EventFormat,
          organizer_id: organizerId,
          expected_attendees: eventData.expected_attendees || null,
          budget_estimate: eventData.budget_estimate || null,
          internal_notes: eventData.internal_notes || null,
        });

        toast({
          title: "Événement créé !",
          description: `L'événement "${eventData.name}" a été créé avec succès.`,
        });

        onEventCreated?.(newEvent.id);
      }

      handleClose();
    } catch (error) {
      toast({
        title: "Erreur",
        description:
          error instanceof Error
            ? error.message
            : "Impossible de créer l'événement",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="flex h-[80vh] max-h-[700px] w-[95vw] max-w-2xl flex-col p-0">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Assistant IA - Gestion du collectif
          </DialogTitle>
          <DialogDescription>
            Créez des événements, ajoutez des membres ou constituez des équipes
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={cn(
                  "flex gap-3",
                  message.role === "user" ? "flex-row-reverse" : "flex-row",
                )}
              >
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  {message.role === "user" ? (
                    <User className="h-4 w-4" />
                  ) : (
                    <Bot className="h-4 w-4" />
                  )}
                </div>
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-4 py-2",
                    message.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted",
                  )}
                >
                  <p className="whitespace-pre-wrap text-sm">
                    {message.content}
                  </p>

                  {message.extractedData?.ready_to_create && (
                    <div className="mt-3 rounded-md border bg-background p-3">
                      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        Prêt à créer
                      </div>
                      <ExtractedDataSummary data={message.extractedData} />
                    </div>
                  )}

                  {message.extractedData &&
                    !message.extractedData.ready_to_create &&
                    message.extractedData.missing_info.length > 0 && (
                      <div className="mt-3 rounded-md border bg-background p-3">
                        <div className="mb-2 flex items-center gap-2 text-sm font-medium text-amber-600">
                          <AlertCircle className="h-4 w-4" />
                          Informations manquantes
                        </div>
                        <ul className="list-inside list-disc text-xs text-muted-foreground">
                          {message.extractedData.missing_info.map((info, i) => (
                            <li key={i}>{info}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 rounded-lg bg-muted px-4 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Analyse en cours...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="border-t px-6 py-4">
          {currentExtraction?.ready_to_create && (
            <div className="mb-4">
              <Button
                onClick={handleCreateEntities}
                disabled={isCreating}
                className="w-full"
                size="lg"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Création en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Créer
                  </>
                )}
              </Button>
            </div>
          )}

          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Collez un email, décrivez un appel, ou tapez les informations..."
              className="min-h-[60px] resize-none"
              disabled={isLoading}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              size="icon"
              className="h-[60px] w-[60px]"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Appuyez sur Entrée pour envoyer, Shift+Entrée pour un saut de ligne
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExtractedDataSummary({ data }: { data: AIResponse }) {
  const items = [];

  if (data.event?.name) {
    items.push({ label: "Événement", value: data.event.name });
  }
  if (data.event?.event_date) {
    items.push({
      label: "Date",
      value: new Date(data.event.event_date).toLocaleDateString("fr-CA"),
    });
  }
  if (data.event?.event_time) {
    items.push({ label: "Heure", value: data.event.event_time });
  }
  if (data.event?.location) {
    items.push({ label: "Lieu", value: data.event.location });
  }
  if (data.event?.event_type?.length) {
    items.push({ label: "Type", value: data.event.event_type.join(", ") });
  }
  if (data.organizer?.name || data.existing_organizer_match) {
    items.push({
      label: "Organisateur",
      value: data.organizer?.name || "Existant",
    });
  }

  if (data.members && data.members.length > 0) {
    items.push({
      label: "Membres",
      value: `${data.members.length} membre(s)`,
    });
  }

  if (data.team?.name) {
    items.push({
      label: "Équipe",
      value: data.team.name,
    });
  }

  return (
    <div className="space-y-1">
      {items.map((item, i) => (
        <div key={i} className="flex justify-between text-xs">
          <span className="text-muted-foreground">{item.label}:</span>
          <span className="font-medium">{item.value}</span>
        </div>
      ))}
    </div>
  );
}
